import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CacheService } from '../cache';
import { ResourceService } from './resource';
import { Resource } from '../../models/resource';
import {
  CreateAdminTravelDocumentDto,
  CreateTravelDocumentDto,
  TravelDocument,
  TravelDocumentApiResponse,
  TravelDocumentUserRequest,
  UpdateTravelDocumentDto
} from '../../models/travel-document';

@Injectable({
  providedIn: 'root'
})
export class TravelDocumentService extends ResourceService<Resource> {

  private readonly TRAVEL_DOCUMENTS_BASE = 'travel-documents';

  constructor(
    protected http: HttpClient,
    protected cache: CacheService
  ) {
    super(http, cache, 'travel-documents');
  }

  private getAdminHeaders(asAdmin: boolean): HttpHeaders | undefined {
    return asAdmin ? new HttpHeaders({ 'x-requested-with': 'admin' }) : undefined;
  }

  addTravelDocumentFullResponse(data: CreateTravelDocumentDto): Observable<TravelDocumentApiResponse> {
    const url = this.TRAVEL_DOCUMENTS_BASE;
    return this.http.post<TravelDocumentApiResponse>(url, data);
  }

  getTravelDocumentById(id: string): Observable<TravelDocumentApiResponse> {
    return this.http.get<TravelDocumentApiResponse>(`${this.TRAVEL_DOCUMENTS_BASE}/${id}`);
  }

  // ====================== ADMIN CREATE DOCUMENT ======================
  /**
   * Admin: create a travel document on behalf of a user
   * POST /travel-documents
   * Passes userId in request headers for admin context
   *
   * @param userId  The ID of the user the document belongs to
   * @param data
   */
  adminCreateDocument(userId: string, data: CreateAdminTravelDocumentDto): Observable<TravelDocumentApiResponse> {

    return this.http.post<TravelDocumentApiResponse>(this.TRAVEL_DOCUMENTS_BASE, data, {   headers: {
      'x-requested-with': 'admin'

    } });
  }
  updateTravelDocumentFull(
  id: string,
  updates: UpdateTravelDocumentDto
): Observable<TravelDocumentApiResponse> {
  return this.http.put<TravelDocumentApiResponse>(
    `${this.TRAVEL_DOCUMENTS_BASE}/${id}`,
    updates
  );
}

// ====================== GET USER DOCUMENTS ======================
  /**
   * Get all travel documents for a specific user
   * GET /api/v2/travel-documents/user/:userId
   */
getUserDocuments(userId: string): Observable<TravelDocument[]> {
    if (!userId) {
      throw new Error('userId is required');
    }

    const url = `${this.TRAVEL_DOCUMENTS_BASE}/user/${userId}`;

    return this.http.get<TravelDocumentApiResponse>(url).pipe(
      map(response => {
        if (response.success !== 1) {
          throw new Error(response.message || 'Failed to fetch user documents');
        }

        // Backend returns "travel_documents" (plural) as array
        return (response.data?.travel_documents || []) as TravelDocument[];
      })
    );
  }

  /**
   * Convenience method: returns full raw API response
   */
  getUserDocumentsFullResponse(userId: string): Observable<TravelDocumentApiResponse>;
  getUserDocumentsFullResponse(request: TravelDocumentUserRequest, asAdmin: true): Observable<TravelDocumentApiResponse>;
  getUserDocumentsFullResponse(
    userIdOrRequest: string | TravelDocumentUserRequest,
    asAdmin: boolean = false
  ): Observable<TravelDocumentApiResponse> {
    const userId = typeof userIdOrRequest === 'string' ? userIdOrRequest : userIdOrRequest?.userId;

    if (!userId?.trim()) {
      throw new Error('userId is required');
    }

    const url = `${this.TRAVEL_DOCUMENTS_BASE}/user/${userId}`;

    const headers = this.getAdminHeaders(asAdmin);

    return this.http.get<TravelDocumentApiResponse>(url, headers ? { headers } : {});
  }

  // ====================== DELETE ======================
  /**
   * Deletes a travel document by ID
   * DELETE /api/v2/travel-documents/:id
   *
   * @param id Document ID to delete
   * @returns Observable<void> - succeeds if deletion was successful
   */
  deleteTravelDocument(id: string): Observable<void>;
  deleteTravelDocument(id: string, request: TravelDocumentUserRequest, asAdmin: true): Observable<void>;
  deleteTravelDocument(
    id: string,
    request?: TravelDocumentUserRequest,
    asAdmin: boolean = false
  ): Observable<void> {
    if (!id?.trim()) {
      throw new Error('Document ID is required');
    }

    if (asAdmin && !request?.userId?.trim()) {
      throw new Error('userId is required');
    }

    const url = `${this.TRAVEL_DOCUMENTS_BASE}/${id}`;

    const headers = this.getAdminHeaders(asAdmin);
    const options: { headers?: HttpHeaders; body?: TravelDocumentUserRequest } = {};

    if (headers) {
      options.headers = headers;
    }

    if (request?.userId?.trim()) {
      options.body = request;
    }

    return this.http.delete<TravelDocumentApiResponse>(url, options).pipe(
      map(response => {
        if (response && response.success !== undefined && response.success !== 1) {
          throw new Error(response.message || 'Failed to delete travel document');
        }
      })
    );
  }


}
